import * as Platform from "@effect/platform";
import * as S from "@effect/schema/Schema";
import { concatBytes } from "@noble/ciphers/utils";
import { BinaryReader, BinaryWriter } from "@protobuf-ts/runtime";
import * as Arr from "effect/Array";
import * as Context from "effect/Context";
import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import { absurd } from "effect/Function";
import * as Option from "effect/Option";
import * as Predicate from "effect/Predicate";
import { Config } from "./Config.js";
import { merkleTreeToString, unsafeMerkleTreeFromString, } from "./Crdt.js";
import { SecretBox } from "./Crypto.js";
import { Owner } from "./Owner.js";
import * as Protobuf from "./Protobuf.js";
export const Sync = Context.GenericTag("Sync");
// To preserve identity.
export const initialSyncState = { _tag: "SyncStateInitial" };
export class SyncFactory extends Context.Tag("SyncFactory")() {
}
export const createSync = Effect.gen(function* () {
    const initContext = Context.empty().pipe(Context.add(SecretBox, yield* SecretBox));
    const afterInitContext = yield* Deferred.make();
    return Sync.of({
        init: (owner) => Effect.logDebug(["Sync init", { owner }]).pipe(Effect.tap(Deferred.succeed(afterInitContext, initContext.pipe(Context.add(Owner, owner))))),
        sync: ({ merkleTree, timestamp, messages }) => Effect.gen(function* () {
            yield* Effect.logDebug([
                "Sync request",
                { merkleTree, timestamp, messages },
            ]);
            const secretBox = yield* SecretBox;
            const owner = yield* Owner;
            const config = yield* Config;
            return yield* Effect.forEach(messages || [], ({ timestamp, ...newMessage }) => Effect.map(secretBox.seal(owner.encryptionKey, newMessageToBinary(newMessage)), (content) => ({ timestamp, content }))).pipe(Effect.map((encrypedMessages) => Protobuf.SyncRequest.toBinary({
                messages: encrypedMessages,
                userId: owner.id,
                nodeId: timestamp.node,
                merkleTree: merkleTreeToString(merkleTree),
            }, binaryWriteOptions)), Effect.flatMap((body) => Effect.gen(function* () {
                const client = (yield* Platform.HttpClient.HttpClient).pipe(Platform.HttpClient.filterStatusOk);
                return yield* Platform.HttpClientRequest.post(config.syncUrl).pipe(Platform.HttpClientRequest.bodyUint8Array(body, "application/x-protobuf"), client.execute, Effect.flatMap((response) => response.arrayBuffer), Effect.scoped);
            }).pipe(Effect.provide(Platform.FetchHttpClient.layer))), Effect.map((buffer) => Protobuf.SyncResponse.fromBinary(new Uint8Array(buffer), binaryReadOptions)), Effect.flatMap((syncResponse) => Effect.forEach(syncResponse.messages, (encrypedMessage) => Effect.map(secretBox.open(owner.encryptionKey, encrypedMessage.content), (binary) => [binary, encrypedMessage.timestamp])).pipe(Effect.map(Arr.filterMap(([binary, timestamp]) => Option.map(newMessageFromBinary(binary), (newMessage) => ({ ...newMessage, timestamp })))), Effect.map((messages) => ({
                messages,
                merkleTree: unsafeMerkleTreeFromString(syncResponse.merkleTree),
            })), Effect.tap((response) => Effect.logDebug(["Sync response", response])))), Effect.catchTag("RequestError", () => Effect.fail({
                _tag: "SyncStateIsNotSynced",
                error: { _tag: "NetworkError" },
            })), Effect.catchTag("ResponseError", ({ response: { status } }) => {
                switch (status) {
                    case 402:
                        return Effect.fail({
                            _tag: "SyncStateIsNotSynced",
                            error: { _tag: "PaymentRequiredError" },
                        });
                    default:
                        return Effect.fail({
                            _tag: "SyncStateIsNotSynced",
                            error: { _tag: "ServerError", status },
                        });
                }
            }));
        }).pipe((effect) => Effect.flatMap(Deferred.await(afterInitContext), (context) => Effect.provide(effect, context))),
    });
});
const newMessageToBinary = ({ value, ...rest }) => concatBytes(version1, Protobuf.MessageContent.toBinary({ value: valueToProtobuf(value), ...rest }, binaryWriteOptions));
const version1 = new Uint8Array([0, 1]);
const valueToProtobuf = (value) => {
    switch (typeof value) {
        case "string":
            return { oneofKind: "stringValue", stringValue: value };
        case "number":
            return {
                oneofKind: "numberValue",
                numberValue: S.encodeSync(S.NumberFromString)(value),
            };
    }
    if (value == null)
        return { oneofKind: undefined };
    if (Predicate.isUint8Array(value))
        return { oneofKind: "bytesValue", bytesValue: value };
    return { oneofKind: "jsonValue", jsonValue: JSON.stringify(value) };
};
// The 'protobuf-ts' uses TextEncoder, but polyfill fast-text-encoding
// doesn't support the fatal option.
// https://github.com/timostamm/protobuf-ts/issues/184#issuecomment-1658443836
const binaryWriteOptions = {
    writerFactory: () => new BinaryWriter({
        encode: (input) => new TextEncoder().encode(input),
    }),
};
const binaryReadOptions = {
    readerFactory: (bytes) => new BinaryReader(bytes, {
        decode: (input) => new TextDecoder().decode(input),
    }),
};
const newMessageFromBinary = (binary) => {
    if (!startsWithArray(binary, version1))
        return Option.none();
    const { value, ...content } = Protobuf.MessageContent.fromBinary(binary.slice(version1.length), binaryReadOptions);
    return Option.some({ value: valueFromProtobuf(value), ...content });
};
const startsWithArray = (array, prefix) => {
    if (prefix.length > array.length)
        return false;
    for (let i = 0; i < prefix.length; i++) {
        if (array[i] !== prefix[i])
            return false;
    }
    return true;
};
const valueFromProtobuf = (value) => {
    switch (value.oneofKind) {
        case "numberValue":
            return S.decodeSync(S.NumberFromString)(value.numberValue);
        case "stringValue":
            return value.stringValue;
        case "bytesValue":
            return value.bytesValue;
        case "jsonValue":
            return JSON.parse(value.jsonValue);
        case undefined:
            return null;
        default:
            return absurd(value);
    }
};
